import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INotesCountWidgetData } from "../models/interfaces";
interface countWidgetState {
	isCountWidgetOpen: boolean; // opening the widget
	isCountWidgetLoading: boolean;  // loading state
	isCountWidgetExpanded: boolean;
	countSelectedType: string[];
	countStudiesFilterOptions: any[];
	countMetaFilterOptions: any[];
	countSelectedStudies: string[];
	countSelectedFileData: string[];
	countSelectedMetaData: string[];
	countSelectedMetaDataLabels: string[];
	isCountWidgetStudiesDrawerOpen: boolean;
	isCountWidgetReferenceDrawerOpen: boolean;
	isCountReferenceDrawerExpanded: boolean;
	countSelectedStudiesId: string[];
	countSelectedQuote: any | null;
	isCountWidgetOpenedPreviously: boolean;
	countWidgetSearchPhrase: string;
	countSelectedWidgetData: any | null; // all data will go here
	countWidgetStreamingQuoteData: any[];
	abortStreamingCountWidget: AbortController | null;
	countWidgets: any[];
	selectedWidgetQueryId: number;
	countWidgetData: { // populate this with the data
		numerator: number;
		denominator: number;
		references: any[];
		transcripts_count: number;
		studies_count: number;
		quotes_count: number;
	};
	countWidgetStreaming: boolean;
	isCountStreamingError: boolean;
	abortStreamingCountChat: AbortController | null;
	countLabels: string[];
	countError: boolean;
	countsSelectedWidgetType: string;
	notesCountWidgetData: INotesCountWidgetData | null;
}

const initialState: countWidgetState = {
	isCountWidgetOpen: false,
	isCountWidgetLoading: false,
	isCountWidgetExpanded: false,
	countSelectedType: [],
	countStudiesFilterOptions: [],
	countMetaFilterOptions: [],
	countSelectedStudies: [],
	countSelectedFileData: [],
	countSelectedMetaData: [],
	countSelectedMetaDataLabels: [],
	isCountWidgetStudiesDrawerOpen: false,
	isCountWidgetReferenceDrawerOpen: false,
	isCountReferenceDrawerExpanded: false,
	countSelectedStudiesId: [],
	countSelectedQuote: null,
	isCountWidgetOpenedPreviously: false,
	countWidgetSearchPhrase: "",
	countSelectedWidgetData: null,
	countWidgetStreamingQuoteData: [],
	abortStreamingCountWidget: null,
	countWidgets: [],
	selectedWidgetQueryId: null,
	countWidgetData: {
		numerator: null,
		denominator: null,
		references: [],
		transcripts_count: null,
		studies_count: null,
		quotes_count: null,
	},
	countWidgetStreaming: false,
	isCountStreamingError: false,
	abortStreamingCountChat: null,
	countLabels: [],
	countError: false,
	countsSelectedWidgetType: null,
	notesCountWidgetData: null,
};

const shouldUpdateSelectedWidget = (state: countWidgetState, widgetId?: string) => !widgetId || state.countSelectedWidgetData?.widget_id === widgetId;

const countWidgetSlice = createSlice({
	name: "countWidget",
	initialState,
	reducers: {
		setCountWidgetOpen(state, action: PayloadAction<boolean>) {
			state.isCountWidgetOpen = action.payload;
		},
		setCountWidgetLoading(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.isCountWidgetLoading = action.payload?.value ?? action.payload;
		},
		setIsCountWidgetExpanded(state, action: PayloadAction<boolean>) {
			state.isCountWidgetExpanded = action.payload;
		},
		setCountSelectedType(state, action: PayloadAction<string[]>) {
			state.countSelectedType = action.payload;
		},
		setCountStudiesFilterOptions(state, action: PayloadAction<any[]>) {
			state.countStudiesFilterOptions = action.payload;
		},
		setCountMetaFilterOptions(state, action: PayloadAction<any[]>) {
			state.countMetaFilterOptions = action.payload;
		},
		setCountSelectedStudies(state, action: PayloadAction<string[]>) {
			state.countSelectedStudies = action.payload;
		},
		setCountSelectedFileData(state, action: PayloadAction<string[]>) {
			state.countSelectedFileData = action.payload;
		},
		setCountSelectedMetaData(state, action: PayloadAction<string[]>) {
			state.countSelectedMetaData = action.payload;
		},
		setIsCountWidgetStudiesDrawerOpen(state, action: PayloadAction<boolean>) {
			state.isCountWidgetStudiesDrawerOpen = action.payload;
		},
		toggleCountWidgetExpanded(state) {
			state.isCountWidgetExpanded = !state.isCountWidgetExpanded;
		},
		setCountSelectedMetaDataLabels(state, action: PayloadAction<string[]>) {
			state.countSelectedMetaDataLabels = action.payload;
		},
		setIsCountWidgetReferenceDrawerOpen(state, action: PayloadAction<boolean>) {
			state.isCountWidgetReferenceDrawerOpen = action.payload;
		},
		setisCountReferenceDrawerExpanded(state, action: PayloadAction<boolean>) {
			state.isCountReferenceDrawerExpanded = action.payload;
		},
		setCountSelectedStudiesId(state, action: PayloadAction<string[]>) {
			state.countSelectedStudiesId = action.payload;
		},
		setCountSelectedQuote(state, action: PayloadAction<any>) {
			state.countSelectedQuote = action.payload;
		},
		setIsCountWidgetOpenedPreviously(state, action: PayloadAction<boolean>) {
			state.isCountWidgetOpenedPreviously = action.payload;
		},
		setCountWidgetSearchPhrase(state, action: PayloadAction<string>) {
			state.countWidgetSearchPhrase = action.payload;
		},
		setCountSelectedWidgetData(state, action: PayloadAction<any>) {
			state.countSelectedWidgetData = action.payload;
		},
		setCountWidgetStreamingQuoteData(state, action: PayloadAction<any[]>) {
			state.countWidgetStreamingQuoteData = action.payload;
		},
		updateAbortStreamingCountWidget(state, action: PayloadAction<AbortController | null>) {
			if (state.abortStreamingCountWidget) {
				try {
					state.abortStreamingCountWidget.abort("Aborted by user");
				} catch (error) {
					console.warn("Failed to abort", error);
				}
			}
			state.abortStreamingCountWidget = action.payload;
		},
		resetCountWidget(state) {
			const currentAbortStreamingCountWidget = state.abortStreamingCountWidget;
			const currentCountWidgets = state.countWidgets;
			const currentAbortStreamingCountChat = state.abortStreamingCountChat;

			// Reset state while preserving countWidgets
			return {
				...initialState,
				abortStreamingCountWidget: currentAbortStreamingCountWidget,
				countWidgets: currentCountWidgets,
				abortStreamingCountChat: currentAbortStreamingCountChat,
			};
		},
		setCountWigets(state, action: PayloadAction<any[]>) {
			state.countWidgets = action.payload;
		},
		setSelectedWidgetQueryId(state, action: PayloadAction<number>) {
			state.selectedWidgetQueryId = action.payload;
		},
		setCountWidgetData(
			state,
			action: PayloadAction<any>
		) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = action.payload?.data ?? action.payload;
		},
		setCountWidgetDataNumerator(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = {
				...state.countWidgetData,
				numerator: action.payload?.value ?? action.payload,
			};
		},
		setCountWidgetDataDenominator(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = {
				...state.countWidgetData,
				denominator: action.payload?.value ?? action.payload,
			};
		},
		setCountWidgetDataTranscriptCount(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = {
				...state.countWidgetData,
				transcripts_count: action.payload?.value ?? action.payload,
			};
		},
		setCountWidgetDataStudiesCount(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = {
				...state.countWidgetData,
				studies_count: action.payload?.value ?? action.payload,
			};
		},
		setCountWidgetDataQuotesCount(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = {
				...state.countWidgetData,
				quotes_count: action.payload?.value ?? action.payload,
			};
		},
		addCountWidgetReferenceData(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetData = {
				...state.countWidgetData,
				references: [...state.countWidgetData.references, action.payload?.reference ?? action.payload],
			};
		},
		resetCountWidgetData(state) {
			state.countWidgetData = {
				numerator: null,
				denominator: null,
				references: [],
				transcripts_count: null,
				studies_count: null,
				quotes_count: null,
			};
		},
		setCountWidgetStreaming(state, action: PayloadAction<any>) {
			if (!shouldUpdateSelectedWidget(state, action.payload?.widgetId)) return;
			state.countWidgetStreaming = action.payload?.value ?? action.payload;
		},
		updateCountsWidgetData(
			state,
			action: PayloadAction<{ widgetId: string; widgetState: string; streamedData: { numerator: number; denominator: number; references: any[] } }>
		) {
			const countWidgetData = state.countWidgets;
			const updatedCountWidgets = countWidgetData.map((widget) => {
				if (widget.widget_id === action.payload.widgetId) {
					return {
						...widget,
						widget_state: action.payload.widgetState,
						widget_data: action.payload.streamedData,
					};
				}
				return widget;
			});
			state.countWidgets = updatedCountWidgets;
		},
		setIsCountStreamingError(state, action: PayloadAction<boolean>) {
			state.isCountStreamingError = action.payload;
		},
		updateAbortStreamingCountChat(state, action: PayloadAction<AbortController | null>) {
			if (state.abortStreamingCountChat) {
				try {
					state.abortStreamingCountChat.abort("Aborted by user");
				} catch (error) {
					console.warn("Failed to abort", error);
				}
			}
			state.abortStreamingCountChat = action.payload;
		},
		setCountLabels(state, action: PayloadAction<string[]>) {
			state.countLabels = action.payload;
		},
		setCountError(state, action: PayloadAction<boolean>) {
			state.countError = action.payload;
		},
		setCountsSelectedWidgetType(state, action: PayloadAction<string>) {
			state.countsSelectedWidgetType = action.payload;
		},
		setNotesCountWidgetData(state, action: PayloadAction<INotesCountWidgetData | null>) {
			state.notesCountWidgetData = action.payload;
		},
		// Merge newly-saved custom-segment tags into the active widget's
		// references so the UI table refreshes without a re-fetch / reopen.
		// Backend keeps fact_tool_cache in sync separately for next session.
		applyCustomSegmentTagsToWidget(
			state,
			action: PayloadAction<{
				newTagsByFile: Record<string, Array<{ tag_category: string; tag_value: string }>>;
				tagCategory?: string;
			}>,
		) {
			const { newTagsByFile, tagCategory } = action.payload || { newTagsByFile: {} };
			if (!newTagsByFile || !Object.keys(newTagsByFile).length) return;
			const merge = (refs: any[]) =>
				(refs || []).map((ref: any) => {
					const fid = String(ref?.file_id || "");
					const additions = newTagsByFile[fid];
					if (!additions || !additions.length) return ref;
					const existing = Array.isArray(ref.transcript_tags) ? ref.transcript_tags : [];
					const seen = new Set<string>(
						existing.map((t: any) => `${(t?.tag_category || "").toLowerCase()}::${(t?.tag_value || "").toLowerCase()}`),
					);
					const merged = [...existing];
					for (const tag of additions) {
						const key = `${(tag.tag_category || "").toLowerCase()}::${(tag.tag_value || "").toLowerCase()}`;
						if (seen.has(key)) continue;
						seen.add(key);
						merged.push({ tag_category: tag.tag_category, tag_value: tag.tag_value, meta_id: null });
					}
					return { ...ref, transcript_tags: merged };
				});
			const trimmedTagCategory = String(tagCategory || "").trim();
			if (state.countWidgetData) {
				state.countWidgetData = {
					...state.countWidgetData,
					references: state.countWidgetData.references?.length
						? merge(state.countWidgetData.references)
						: state.countWidgetData.references,
					custom_segments_saved: true,
					...(trimmedTagCategory ? { custom_segment_tag_category: trimmedTagCategory } : {}),
				} as any;
			}
			if (state.countSelectedWidgetData) {
				state.countSelectedWidgetData = {
					...state.countSelectedWidgetData,
					references: state.countSelectedWidgetData.references?.length
						? merge(state.countSelectedWidgetData.references)
						: state.countSelectedWidgetData.references,
					custom_segments_saved: true,
					...(trimmedTagCategory ? { custom_segment_tag_category: trimmedTagCategory } : {}),
				};
			}
		},
	},
});
export const {
	setCountWidgetOpen,
	setCountWidgetLoading,
	setIsCountWidgetExpanded,
	setCountSelectedType,
	setCountStudiesFilterOptions,
	setCountMetaFilterOptions,
	setCountSelectedStudies,
	setCountSelectedFileData,
	setCountSelectedMetaData,
	setIsCountWidgetStudiesDrawerOpen,
	toggleCountWidgetExpanded,
	setCountSelectedMetaDataLabels,
	setIsCountWidgetReferenceDrawerOpen,
	setisCountReferenceDrawerExpanded,
	setCountSelectedStudiesId,
	setCountSelectedQuote,
	setIsCountWidgetOpenedPreviously,
	setCountWidgetSearchPhrase,
	setCountSelectedWidgetData,
	setCountWidgetStreamingQuoteData,
	updateAbortStreamingCountWidget,
	resetCountWidget,
	setCountWigets,
	setSelectedWidgetQueryId,
	setCountWidgetData,
	setCountWidgetDataNumerator,
	setCountWidgetDataDenominator,
	addCountWidgetReferenceData,
	resetCountWidgetData,
	setCountWidgetStreaming,
	updateCountsWidgetData,
	setIsCountStreamingError,
	updateAbortStreamingCountChat,
	setCountLabels,
	setCountWidgetDataTranscriptCount,
	setCountWidgetDataStudiesCount,
	setCountWidgetDataQuotesCount,
	setCountError,
	setCountsSelectedWidgetType,
	setNotesCountWidgetData,
	applyCustomSegmentTagsToWidget,
} = countWidgetSlice.actions;

export default countWidgetSlice.reducer;
